import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCanvaIntegration } from '../hooks/useCanvaIntegration';

export const CanvaEditorOverlay: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromEditUrl = params.get('editUrl') || undefined;
  const postId = params.get('postId') || undefined;
  const [editUrl, setEditUrl] = useState<string | undefined>(fromEditUrl);
  const [failedEmbed, setFailedEmbed] = useState(false);
  const { createNewDesign } = useCanvaIntegration();

  useEffect(() => {
    const boot = async () => {
      if (!editUrl) {
        const url = await createNewDesign();
        if (url) setEditUrl(url);
        else navigate(-1);
      }
    };
    void boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const title = useMemo(() => t('canva.editor', 'Canva Editor'), [t]);

  return (
    <div className="fixed inset-0 z-[100] bg-white">
      <div className="flex items-center justify-between px-4 h-12 border-b border-gray-200">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-800"
            aria-label={t('common.close', 'Close')}
          >
            ×
          </button>
          <div className="text-sm text-gray-600 truncate" title={editUrl}>{title}</div>
        </div>
        <div className="flex items-center gap-2">
          {editUrl && (
            <button
              onClick={() => window.open(editUrl, '_blank', 'noopener,noreferrer')}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              {t('common.openInNewTab', 'Open in new tab')}
            </button>
          )}
        </div>
      </div>

      <div className="w-full h-[calc(100vh-48px)] relative">
        {editUrl ? (
          <>
            <iframe
              src={editUrl}
              className="w-full h-full"
              title="Canva Editor"
              allow="clipboard-read; clipboard-write; fullscreen; autoplay; encrypted-media; picture-in-picture; web-share"
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-presentation allow-downloads allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
              onError={() => setFailedEmbed(true)}
              onLoad={() => setFailedEmbed(false)}
            />
            {failedEmbed && (
              <div className="absolute inset-0 bg-white/95 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-gray-700 mb-3">{t('canva.embedBlocked', 'The editor refused to load in an embedded view.')}</p>
                  <button
                    onClick={() => window.open(editUrl, '_blank', 'noopener,noreferrer')}
                    className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    {t('actions.openInCanva', 'Open in Canva')}
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CanvaEditorOverlay;


